import { getLibraryFolders } from "@/api";
import { IAttachmentOption } from "@/api/attachments/attachments.types";
import {
    ILibraryListItem,
    LibraryTab,
} from "@/api/library-documents/library-documents.types";
import { Modal } from "@/components/shared/Modal";
import { DocumentList } from "@/pages/frontend/documents/components/DocumentList";
import { FilterBar } from "@/pages/frontend/documents/components/FilterBar";
import { LibraryTypeFilter } from "@/pages/frontend/documents/utils/libraryItemTypes";
import { libraryItemToAttachmentOption } from "@/utils/library";
import {
    DirectDocumentBudgetNotice,
    DirectDocumentBudgetRetryBanner,
    partitionSelectionByBudget,
    useOptionalDirectDocumentBudget,
} from "@/components/shared/direct-document-budget";
import { Box, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

interface LibrarySelectionModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (attachments: IAttachmentOption[]) => void;
}

const noop = () => {};

const TYPE_FILTER: LibraryTypeFilter = "documents";

export const LibrarySelectionModal = ({
    open,
    onClose,
    onConfirm,
}: LibrarySelectionModalProps) => {
    const [activeTab, setActiveTab] = useState<LibraryTab>("documents");
    const [searchValue, setSearchValue] = useState("");
    const [folderId, setFolderId] = useState<number | null>(null);
    const [selectedItems, setSelectedItems] = useState<ILibraryListItem[]>([]);
    const [listKey, setListKey] = useState(0);

    const handleItemSelectionChange = useCallback(
        (items: ILibraryListItem[]) => {
            setSelectedItems(items);
        },
        []
    );

    const { data: foldersData } = useQuery({
        queryKey: ["frontend-library-folders"],
        queryFn: () => getLibraryFolders(),
        enabled: open,
    });
    const folders = useMemo(
        () => foldersData?.folders ?? [],
        [foldersData?.folders]
    );

    const currentFolder = useMemo(
        () => folders.find((f) => f.id === folderId) ?? null,
        [folders, folderId]
    );

    const resetSelection = useCallback(() => {
        setSelectedItems([]);
        setListKey((k) => k + 1);
    }, []);

    const handleTabChange = useCallback((tab: LibraryTab) => {
        setActiveTab(tab);
        setFolderId(null);
        setSearchValue("");
        setSelectedItems([]);
        setListKey((k) => k + 1);
    }, []);

    const handleFolderNavigate = useCallback((id: number | null) => {
        setFolderId(id);
    }, []);

    const handleSearchClear = useCallback(() => setSearchValue(""), []);

    const handleClose = () => {
        setActiveTab("documents");
        setSearchValue("");
        setFolderId(null);
        resetSelection();
        onClose();
    };

    // Dedup-aware, because this picker yields real ids: a document the user
    // already attached is free to re-select, and charging for it would block
    // them below the real cap. The count-based path belongs to the upload
    // modal, whose files have no ids yet.
    const budget = useOptionalDirectDocumentBudget();
    const countedSelectedIds = useMemo(
        () =>
            partitionSelectionByBudget(
                selectedItems.filter((item) => item.type === "document")
            ).counted,
        [selectedItems]
    );
    const showLimitUi = budget != null && activeTab === "documents";
    const evaluation = showLimitUi ? budget.evaluate(countedSelectedIds) : null;
    const budgetBlocks = evaluation != null && !evaluation.admissible;
    const budgetUnavailable =
        showLimitUi &&
        (budget.status === "pending" ||
            budget.status === "error" ||
            budget.limit == null) &&
        countedSelectedIds.length > 0;

    const handleConfirm = () => {
        if (budgetBlocks || budgetUnavailable) return;
        const attachments: IAttachmentOption[] = selectedItems.map((item) =>
            libraryItemToAttachmentOption(item, {
                isKnowledgeBaseDefault: activeTab === "knowledge_base",
            })
        );
        handleClose();
        onConfirm(attachments);
    };

    const selectedCount = selectedItems.length;

    const footerText =
        selectedCount > 0 ? `${selectedCount} selected` : undefined;

    const attachLabel =
        selectedCount > 0 ? `Attach (${selectedCount})` : "Attach";

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Attach from Library"
            subtitle="Select documents to attach to your message"
            maxWidth="lg"
            showCancel
            footerText={footerText}
            actions={[
                {
                    label: attachLabel,
                    onClick: handleConfirm,
                    disabled:
                        selectedCount === 0 ||
                        budgetBlocks ||
                        budgetUnavailable,
                },
            ]}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "60vh",
                    minHeight: 400,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    <FilterBar
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        onSearchClear={handleSearchClear}
                        typeFilter={TYPE_FILTER}
                        currentFolder={currentFolder}
                        folders={folders}
                        onFolderNavigate={handleFolderNavigate}
                        hideActions
                        hideTypeFilter
                    />
                </Box>
                {showLimitUi && (
                    <Stack spacing={1} sx={{ flexShrink: 0, pt: 1, pb: 1 }}>
                        {budget.status === "error" && (
                            <DirectDocumentBudgetRetryBanner
                                onRetry={budget.retry}
                                isRetrying={budget.isRetrying}
                            />
                        )}
                        {budgetBlocks && evaluation != null && (
                            <DirectDocumentBudgetNotice
                                variant="would-exceed"
                                limit={evaluation.limit}
                                newCount={evaluation.proposedCount}
                            />
                        )}
                    </Stack>
                )}
                <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <DocumentList
                        key={listKey}
                        activeTab={activeTab}
                        folderId={folderId}
                        searchQuery={searchValue}
                        typeFilter={TYPE_FILTER}
                        mode="select"
                        selectionMode="multi"
                        tableDensity="compact"
                        onFolderNavigate={handleFolderNavigate}
                        onItemSelectionChange={handleItemSelectionChange}
                        onDocumentAction={noop}
                        hideRowActions
                    />
                </Box>
            </Box>
        </Modal>
    );
};
